import { useState, useCallback } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { fetchDeleteSiteImage, fetchSiteImages } from '@/features/sites/sitesThunks';
import { SiteImages } from '@/types/site.types';
import { UseSnackbarReturn } from '@/hooks/useSnackbar';

interface UseImageDeleteParams {
    images: SiteImages[];
    selectedSite: string | null;
    snackbar: UseSnackbarReturn;
}

interface UseImageDeleteReturn {
    deleteDialogOpen: boolean;
    imageToDelete: string | null;
    isDeleting: boolean;
    handleDeleteImage: (imageUrl: string) => void;
    confirmDelete: () => Promise<void>;
    cancelDelete: () => void;
}

/**
 * Custom hook to handle image deletion flow including confirmation dialog
 * @param params - Configuration including images array, selected site, and snackbar
 * @returns Delete state and handler functions
 */
export const useImageDelete = ({
    images,
    selectedSite,
    snackbar
}: UseImageDeleteParams): UseImageDeleteReturn => {
    const dispatch = useAppDispatch();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteImage = useCallback((imageUrl: string) => {
        setImageToDelete(imageUrl);
        setDeleteDialogOpen(true);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!imageToDelete) {
            setDeleteDialogOpen(false);
            return;
        }

        const image = images.find(img => img.imageUrl === imageToDelete);

        if (!image || !image.id) {
            snackbar.show('Error: No se encontró el ID de la imagen para eliminar.', 'error');
            setDeleteDialogOpen(false);
            setImageToDelete(null);
            return;
        }

        setIsDeleting(true);
        try {
            await dispatch(fetchDeleteSiteImage({ 
                imageId: image.id, 
                imageName: image.imageUrl 
            })).unwrap();

            snackbar.show('Imagen eliminada correctamente', 'success');
            dispatch(fetchSiteImages(selectedSite));
            setDeleteDialogOpen(false);
            setImageToDelete(null);
        } catch (error: any) {
            snackbar.show(error.message || 'Error al eliminar la imagen', 'error');
            setDeleteDialogOpen(false);
            setImageToDelete(null);
        } finally {
            setIsDeleting(false);
        }
    }, [dispatch, snackbar, imageToDelete, selectedSite, images]);

    const cancelDelete = useCallback(() => {
        setDeleteDialogOpen(false);
        setImageToDelete(null);
    }, []);

    return {
        deleteDialogOpen,
        imageToDelete,
        isDeleting,
        handleDeleteImage,
        confirmDelete,
        cancelDelete
    };
};
